/**
 * ADR-0263 §8 — "Agency plan request" banner + intent processor.
 *
 * Rendered on the dashboard home ONCE an authenticated session exists.
 * On mount it reads `sill_plan_intent` from localStorage; if the plan
 * slug starts with `agency-`, it posts to `/v1/account/agency-request`
 * exactly once per load, then:
 *
 *   - 202 / 409 / any 4xx → clear the localStorage intent (the server
 *     either accepted the request or terminally refused the slug;
 *     retrying on the next load fixes nothing).
 *   - network error → keep the intent so the next load retries.
 *
 * After a successful POST (or when an already-dismissible notice is
 * remembered) the banner shows a small dismissible "Your Agency N
 * request is in." notice. The notice is session-local — a full
 * sign-out + sign-in on a new device does NOT re-fire the POST because
 * the intent is browser-local by design.
 *
 * Gated on `auth.state.kind === 'authenticated'` by the parent view
 * (Dashboard only renders after auth resolves). This component never
 * fires on `/welcome` / `/verify` / `/support-login` / `/connect/*`
 * because those pre-cookie paths never mount Dashboard.
 */

import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { postAgencyRequest } from '../../api/account-agency-request.js';
import {
  clearPlanIntent,
  isAgencyPlanSlug,
  readPlanIntent,
  type PlanIntent,
} from '../../lib/agency-plan-intent.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/** localStorage key for the dismiss flag. Per-browser, per-account via
 *  the intent's plan slug so a fresh request after a dismiss still
 *  shows the notice. */
const DISMISS_KEY_PREFIX = 'sill_agency_request_dismissed:';

function dismissKey(plan: string): string {
  return `${DISMISS_KEY_PREFIX}${plan}`;
}

function readDismissed(plan: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(dismissKey(plan)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(plan: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(dismissKey(plan), '1');
  } catch {
    // best effort — a storage-disabled browser just re-shows the notice.
  }
}

/** Human label for the agency tier based on its slug. Slug carries the
 *  canonical size: `agency-10`, `agency-25`, `agency-50`. Unknown
 *  shapes fall back to a plain "Agency" label. */
function labelForSlug(slug: string): string {
  const m = slug.match(/^agency-(\d+)$/);
  return m !== null ? `Agency ${m[1]}` : 'Agency';
}

export function AgencyRequestBanner(): JSX.Element | null {
  const C = useC();
  // `useOptionalHttp` returns null in tests that render the Dashboard
  // without an HttpProvider (the pre-ADR-0115 mock harnesses). In
  // production the provider is always mounted; we no-op when it is
  // absent so the banner never throws out of its own effect.
  const http = useOptionalHttp();
  const [notice, setNotice] = useState<PlanIntent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(false);
  // StrictMode / effect-deps may re-fire the mount effect; the ref
  // pins the POST to ONE call per page load.
  const firedRef = useRef<boolean>(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (typeof window === 'undefined') return;
    if (http === null) return;
    const intent = readPlanIntent(window.localStorage);
    if (intent === null) return;
    if (!isAgencyPlanSlug(intent.plan)) return;

    // Remember the dismissal state for THIS plan before the POST — a
    // user who dismissed the notice before should not see it re-appear
    // on a fresh retry.
    setDismissed(readDismissed(intent.plan));

    (async (): Promise<void> => {
      const result = await postAgencyRequest(http, {
        plan_slug: intent.plan,
        interval: intent.interval,
      });
      if (result.kind === 'ok') {
        setNotice(intent);
        clearPlanIntent(window.localStorage);
        return;
      }
      if (
        result.kind === 'network_error'
        || result.kind === 'rate_limited'
        || (result.kind === 'forbidden' && result.reason === 'agency_self_signup_disabled')
      ) {
        // Keep the intent and retry on a later load (it expires after
        // 7 days): a blip, a rate limit, or the switch being off right
        // now are not answers about this request.
        return;
      }
      // 404 / 400 / other 403 — terminal for this intent. Clear so we
      // don't spam the server on every load. The notice is not shown;
      // the server-side state carries the reason (the ops panel is
      // quiet about a 404'd slug by design).
      clearPlanIntent(window.localStorage);
    })().catch(() => {
      // Defensive — the client already maps every path to a result.
      // A stray rejection should not break the dashboard load.
    });
  }, [http]);

  if (notice === null || dismissed) return null;

  const label = labelForSlug(notice.plan);

  return (
    <div
      className="p-3 mb-3 flex items-start gap-2 flex-wrap"
      style={{
        background: C.limeDim,
        border: `1px solid ${C.lime}`,
        borderRadius: 2,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          color: C.limeFg,
          padding: '2px 6px',
          background: C.surface,
          border: `1px solid ${C.lime}`,
          borderRadius: 2,
          marginTop: 1,
        }}
      >
        AGENCY
      </div>
      <div
        className="flex-1 min-w-[260px]"
        style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text, lineHeight: 1.5 }}
      >
        <strong>Your {label} request is in.</strong> We&apos;ll set up your agency plan and email you.
      </div>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          writeDismissed(notice.plan);
        }}
        className="flex-shrink-0"
        style={{
          background: 'transparent',
          border: 'none',
          color: C.muted,
          cursor: 'pointer',
          padding: 4,
        }}
        aria-label="Dismiss"
      >
        <X size={13} />
      </button>
    </div>
  );
}
